import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { selectSettingLogo } from 'src/app/stores/settings';

import { SpacesDispensesHD } from '../../interfaces/IHDspace';
import { LockerActionDataService } from '../../services/locker-action-data/locker-action-data.service';
import {
  BUTTON_POSITION,
  CLOSED_SECURE_SPACE_STATE,
  DISPENSE_STATES,
  EMPTY_PRESENCE_STATE,
  LOCKDOWN_ORDER_STATE,
  RETAIL_SCREEN,
  SPACE_DIRECTION,
  SPACE_STATES,
  VIEW_CALLING,
} from '../../utils/constants';
import { Utils } from '../../utils/utils';

export interface IButtonAction {
  text: string;
  action: VIEW_CALLING;
}

@Component({
  selector: 'frodo-space-detail',
  templateUrl: './space-detail.component.html',
  styleUrls: ['./space-detail.component.scss'],
})
export class SpaceDetailComponent implements OnInit, OnDestroy {
  title: string;
  space: SpacesDispensesHD;
  logo: string;
  logoAlt = 'logo';
  dispenseInfo;
  spaceId: string;
  positionLabel: string;
  spaceState: string;
  spacePresence: string;
  spaceSecure: string;
  scheduleName = '';
  scheduleId: string;
  orderState: string;
  dispenseId: string;
  description: string;
  currentIndex: number;
  columnStructure: SpacesDispensesHD[];
  nextDirection: string = SPACE_DIRECTION.Next;
  previousDirection: string = SPACE_DIRECTION.Previous;
  subscription: Subscription = new Subscription();

  private spacesStructures: Array<object>;
  private spaceDetailLockeddown = 'common.spaceDetail.lockeddown';
  private spaceDetailReclaim = 'common.spaceDetail.reclaim';
  private spaceDetailPickup = 'common.spaceDetail.pickup';
  private spaceDetailLoadedVerify = 'common.spaceDetail.loadedVerify';
  private spaceDetailReset = 'common.spaceDetail.reset';
  private spaceDetailDisable = 'common.spaceDetail.disable';
  private spaceDetailEnable = 'common.spaceDetail.enable';
  private spaceDetailOpenDoor = 'common.spaceDetail.openDoor';

  constructor(
    private store: Store,
    private lockerActionDataService: LockerActionDataService,
    private utils: Utils
  ) {
    this.columnStructure = [];
    this.currentIndex = -1;
  }

  ngOnInit() {
    this.subscription.add(
      this.store.select(selectSettingLogo).subscribe((logo) => {
        this.logo = logo;
      })
    );
    this.spacesStructures = this.lockerActionDataService.sortedSpaces;
    this.space = this.lockerActionDataService.selectSpaceInfo;
    this.setValues(this.space);
  }

  setValues(spaceInfo: SpacesDispensesHD): void {
    this.space = spaceInfo;
    if (this.space?.state) {
      this.orderState = 'common.spaceDetail.stateSelected';
      this.spaceId = this.space.id;
      this.positionLabel = this.space.hardwareInfo.position;
      this.spacePresence =
        this.space.state.presence === SPACE_STATES.UNFILL
          ? EMPTY_PRESENCE_STATE
          : this.space.state.presence;
      this.spaceSecure =
        this.space.state.secure === SPACE_STATES.CLOSE
          ? CLOSED_SECURE_SPACE_STATE
          : this.space.state.secure;
      this.spaceState = this.mappingSpaceState();
      this.dispenseInfo = [];
      this.setStructure();
      if (this.space.dispenses.length) {
        this.dispenseInfo = this.space.dispenses[0];
        this.scheduleId = this.dispenseInfo.schedule;
        this.orderState = this.isOrderLockeddown()
          ? this.capitalizeFirstLetter(LOCKDOWN_ORDER_STATE)
          : this.capitalizeFirstLetter(this.dispenseInfo.state);
        this.dispenseId = this.dispenseInfo.dispense;
        this.scheduleName = this.dispenseInfo.scheduleInfo.scheduleName;
        this.description = this.dispenseInfo.scheduleInfo.description;
      }
    }
  }

  private checkDispenseState(state: string): boolean {
    return this.dispenseInfo?.state === state;
  }

  isOrderAssigned(): boolean {
    return !!this.space?.dispenses?.length;
  }

  isOrderLoaded(): boolean {
    return this.checkDispenseState(DISPENSE_STATES.LOADED);
  }

  isOrderLoadedVerify(): boolean {
    return this.checkDispenseState(DISPENSE_STATES.LOADED_VERIFY);
  }

  isOrderLockeddown(): boolean {
    return this.checkDispenseState(DISPENSE_STATES.LOCKEDDOWN);
  }

  isSpaceDisabled(): boolean {
    return this.space?.state?.selectable === SPACE_STATES.DISABLE;
  }

  setActionByState(positionButton: string): IButtonAction {
    const positionMap = {
      [BUTTON_POSITION.UpLeft]: this.getButtonActionUpLeft(),
      [BUTTON_POSITION.UpRight]: this.getButtonActionUpRight(),
      [BUTTON_POSITION.Down]: this.getButtonActionDown(),
    };
    return positionMap[positionButton];
  }

  private getButtonActionUpLeft(): IButtonAction {
    return this.isOrderLoaded()
      ? { text: this.spaceDetailPickup, action: VIEW_CALLING.DISPENSE_ORDER }
      : { text: this.spaceDetailReset, action: VIEW_CALLING.RESET_SPACE };
  }

  private getButtonActionUpRight(): IButtonAction {
    return this.isOrderLoaded()
      ? { text: this.spaceDetailReclaim, action: VIEW_CALLING.RECLAIM_ORDER }
      : { text: this.spaceDetailDisable, action: VIEW_CALLING.DISABLE_SPACE };
  }

  private getButtonActionDown(): IButtonAction {
    if (this.isOrderLockeddown()) {
      return {
        text: this.spaceDetailReclaim,
        action: VIEW_CALLING.RECLAIM_ORDER,
      };
    } else if (this.isOrderLoaded()) {
      return {
        text: this.spaceDetailLockeddown,
        action: VIEW_CALLING.LOCKDOWN_ORDER,
      };
    } else if (this.isOrderLoadedVerify()) {
      return {
        text: this.spaceDetailLoadedVerify,
        action: VIEW_CALLING.VERIFY_ORDER,
      };
    } else if (this.isSpaceDisabled()) {
      return {
        text: this.spaceDetailEnable,
        action: VIEW_CALLING.ENABLE_SPACE,
      };
    } else if (!this.isOrderAssigned()) {
      return { text: this.spaceDetailOpenDoor, action: VIEW_CALLING.OPEN_DOOR };
    } else {
      return { text: this.spaceDetailReset, action: VIEW_CALLING.RESET_SPACE };
    }
  }

  isUpLeftButtonEnabled(): boolean {
    const spaceState = this.space?.state;

    return (
      spaceState?.selectable === SPACE_STATES.RESERVE ||
      spaceState?.presence === SPACE_STATES.FILL ||
      spaceState?.secure === SPACE_STATES.OPEN
    );
  }

  isDownButtonEnabled(): boolean {
    return (
      this.space?.state?.secure === SPACE_STATES.CLOSE ||
      this.isOrderAssigned() ||
      this.isSpaceDisabled()
    );
  }

  showUpButtons(): boolean {
    return (!this.isOrderAssigned() && !this.isSpaceDisabled()) || this.isOrderLoaded();
  }

  capitalizeFirstLetter(state: string): string {
    return String(state).charAt(0).toUpperCase() + String(state).slice(1);
  }

  goBack(): void {
    this.utils.redirect(RETAIL_SCREEN.STATUS);
  }

  action(action: string): void {
    this.utils.redirect(RETAIL_SCREEN.CONFIRMATION, { viewCalling: action });
  }

  mappingSpaceState(): string {
    if (this.isSpaceDisabled()) {
      return `${this.capitalizeFirstLetter(this.space.state.selectable)}`;
    } else {
      return `${this.capitalizeFirstLetter(this.spaceSecure)}/${this.capitalizeFirstLetter(
        this.spacePresence
      )}/${this.capitalizeFirstLetter(this.space.state.selectable)}`;
    }
  }

  setStructure(): void {
    const positionLetter = this.positionLabel.charAt(0);
    const structure = this.spacesStructures.find((space) => positionLetter in space)?.[
      positionLetter
    ];

    if (!structure) return;

    const cleanStructure = this.removeHmiInfo(structure);

    for (const currentSpace of cleanStructure) {
      const foundIndex = currentSpace.findIndex(
        (space) => space.hardwareInfo.position === this.positionLabel
      );
      if (foundIndex !== -1) {
        this.columnStructure = currentSpace;
        this.currentIndex = foundIndex;
        break;
      }
    }
  }

  removeHmiInfo(structure: Array<SpacesDispensesHD[]>): Array<SpacesDispensesHD[]> {
    return structure.map((column) => column.filter((space: SpacesDispensesHD) => !!space.id));
  }

  updateSpace(direction: string): void {
    const offset = direction === this.nextDirection ? 1 : -1;
    const spaceInfo = this.columnStructure[this.currentIndex + offset];
    this.lockerActionDataService.selectSpaceInfo = spaceInfo;
    this.setValues(spaceInfo);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
